/**
 * Textarea — labelled textarea matching the Input component style.
 */

import { forwardRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?:  string;
  characterCount?: number;
  maxCharacters?:  number;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, characterCount, maxCharacters, className, id, ...props }, ref) => {
    const fieldId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const isOverLimit = maxCharacters !== undefined && (characterCount ?? 0) > maxCharacters;

    return (
      <div className="flex flex-col gap-1.5 w-full">
        <div className="flex items-center justify-between">
          {label && (
            <label htmlFor={fieldId} className="text-sm font-medium text-surface-700">
              {label}
              {props.required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
          )}
          {maxCharacters !== undefined && (
            <span className={cn(
              'text-xs tabular-nums',
              isOverLimit ? 'text-red-500 font-medium' : 'text-surface-400'
            )}>
              {characterCount ?? 0} / {maxCharacters}
            </span>
          )}
        </div>

        <textarea
          ref={ref}
          id={fieldId}
          className={cn(
            'block w-full rounded-lg border bg-white text-sm text-surface-900',
            'placeholder:text-surface-400 resize-y',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'disabled:bg-surface-50 disabled:text-surface-500 disabled:cursor-not-allowed',
            error
              ? 'border-red-400 focus:ring-red-400 focus:border-red-400'
              : 'border-surface-300 hover:border-surface-400',
            'px-3 py-2.5',
            className
          )}
          {...props}
        />

        {error && <p className="text-xs text-red-600">{error}</p>}
        {!error && hint && <p className="text-xs text-surface-500">{hint}</p>}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
export default Textarea;
